/** @module State of one review case: the chosen card, variant and language, and the decisions. */
import { useMutation } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { api, ApiError, type components, problemFrom } from "@/api/client";
import { type Quad, toPixels } from "@/features/review/corner-editor";
import { type Choice, defaultVariant } from "@/components/resolve-form";
import { printedAs, readNumber, useCard } from "@/lib/cards";
import { useAuthedImage } from "@/lib/images";

export type Scan = components["schemas"]["ScanOut"];

/** The backend stores the candidates as free-form JSON; this is their actual shape. */
export interface Candidate {
  print_id: string;
  score: number;
  from_ocr?: boolean;
  /** Chosen by hand in the search, so it has no score of its own. */
  picked?: boolean;
}

export interface ReadNumber {
  number?: string;
  total?: string;
  set_code?: string;
}

/** What happened to a case, so the page can say it and offer to undo it. */
export interface Outcome {
  kind: "confirmed" | "dismissed" | "requeued" | "taken" | "approved" | "excluded";
  /** "Duokles · PAR 133" for the toast. */
  label?: string;
}

/** How many suggestions the keys 1–n and the candidate row offer. */
export const SHOWN = 5;

/**
 * Keys meant for something else: a field, or a focused button other than a card tile.
 *
 * Enter on a focused "Verwerfen" must discard, never confirm; a tile only selects a card.
 */
export function isTyping(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  if (["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName)) return true;
  return ["BUTTON", "A"].includes(target.tagName) && !("tile" in target.dataset);
}

/** Does the number read from the photo back this candidate? Shown, never decided, here. */
export function ocrBacks(scan: Scan, candidate: Candidate): boolean | null {
  const read = readNumber(scan.result?.ocr);
  if (!read) return null;
  if (candidate.from_ocr) return true;
  return scan.result?.ocr_confirms === true && candidate.print_id === scan.result.chosen_print_id;
}

/** A tester's booked card waiting for release: what they chose is the starting point. */
export interface Approval {
  printId: string;
  variantId: number | null;
  language: string | null;
}

export function useReviewCase(scan: Scan, onDone: (outcome: Outcome) => void, approval?: Approval) {
  const { i18n } = useTranslation();
  const candidates = (scan.result?.candidates ?? []) as unknown as Candidate[];
  const [chosen, setChosen] = useState<string | null>(
    approval?.printId ?? candidates[0]?.print_id ?? null,
  );
  const [editing, setEditing] = useState(false);
  const [comparing, setComparing] = useState(false);
  const [searching, setSearching] = useState(false);
  const [choice, setChoice] = useState<Choice>({
    variantId: approval?.variantId ?? scan.result?.chosen_variant_id ?? null,
    language:
      approval?.language ?? scan.result?.language_suggestion ?? i18n.resolvedLanguage ?? "de",
    condition: "NM",
  });
  // Screens get a 1280 px copy: the originals are megabytes and took seconds per card.
  const photo = useAuthedImage(`/api/v1/scans/${scan.id}/crop?size=large`);
  // The comparison shows the whole photo; the corner editor needs its true pixel size.
  const original = useAuthedImage(
    editing
      ? `/api/v1/scans/${scan.id}/photo`
      : comparing
        ? `/api/v1/scans/${scan.id}/photo?size=large`
        : null,
  );
  const card = useCard(chosen);

  // Picking another candidate means another card, so the variant follows along.
  // Deriving it beats keeping it in sync: there is only one source of truth.
  const variants = card.data?.variants ?? [];
  const suggested =
    approval?.printId === chosen
      ? approval.variantId
      : chosen === scan.result?.chosen_print_id
        ? (scan.result.chosen_variant_id ?? null)
        : null;
  const variantId = variants.some((variant) => variant.id === choice.variantId)
    ? choice.variantId
    : (defaultVariant(variants, suggested)?.id ?? null);
  const effective: Choice = { ...choice, variantId };
  const label = card.data ? `${card.data.name} · ${printedAs(card.data, "")}` : (chosen ?? "");

  const settledElsewhere = (cause: unknown) => {
    // Somebody else was faster, or this is a second tap: either way it is settled.
    if (cause instanceof ApiError && cause.code === "scan_already_resolved") {
      onDone({ kind: "taken" });
    }
  };

  const resolve = useMutation({
    mutationFn: async () => {
      if (!chosen || !variantId) throw new Error("no variant");
      if (approval) {
        // Releasing a label: a different card corrects the tester's copy on the server.
        const { data, response } = await api.POST("/api/v1/admin/scans/{scan_id}/approve", {
          params: { path: { scan_id: scan.id } },
          body: { print_id: chosen, variant_id: variantId, language: effective.language },
        });
        if (!data) throw problemFrom(response);
        return data;
      }
      const { data, response } = await api.POST("/api/v1/scans/{scan_id}/resolve", {
        params: { path: { scan_id: scan.id } },
        body: {
          print_id: chosen,
          variant_id: variantId,
          language: effective.language,
          condition: effective.condition,
        },
      });
      if (!data) throw problemFrom(response);
      return data;
    },
    onSuccess: () => {
      onDone({ kind: approval ? "approved" : "confirmed", label });
    },
    onError: settledElsewhere,
  });

  const dismiss = useMutation({
    mutationFn: async () => {
      if (approval) {
        // A doubtful label stays out of training; the tester keeps their card.
        const { data, response } = await api.PATCH("/api/v1/admin/scans/{scan_id}", {
          params: { path: { scan_id: scan.id } },
          body: { training_excluded: true },
        });
        if (!data) throw problemFrom(response);
        return data;
      }
      const { data, response } = await api.POST("/api/v1/scans/{scan_id}/dismiss", {
        params: { path: { scan_id: scan.id } },
      });
      if (!data) throw problemFrom(response);
      return data;
    },
    onSuccess: () => {
      onDone({ kind: approval ? "excluded" : "dismissed" });
    },
    onError: settledElsewhere,
  });

  const recognise = useMutation({
    mutationFn: async ({ quad, size }: { quad: Quad; size: { width: number; height: number } }) => {
      const { data, response } = await api.POST("/api/v1/scans/{scan_id}/quad", {
        params: { path: { scan_id: scan.id } },
        body: { quad: toPixels(quad, size.width, size.height) },
      });
      if (!data) throw problemFrom(response);
      return data;
    },
    onSuccess: () => {
      onDone({ kind: "requeued" });
    },
  });

  // A card picked by hand belongs in the row as well, otherwise the choice is invisible.
  const picked: Candidate[] =
    chosen && !candidates.some((candidate) => candidate.print_id === chosen)
      ? [{ print_id: chosen, score: 0, picked: true }]
      : [];
  const shown = [...picked, ...candidates.slice(0, SHOWN)];
  const busy = resolve.isPending || dismiss.isPending || recognise.isPending;
  const settled = resolve.isSuccess || dismiss.isSuccess;
  const canConfirm = Boolean(chosen && variantId) && !busy && !settled;

  /** The next suggestion; after the last one the search takes over, as in the design. */
  const nextCandidate = () => {
    const at = shown.findIndex((candidate) => candidate.print_id === chosen);
    const next = shown[at + 1];
    if (next) setChosen(next.print_id);
    else setSearching(true);
  };
  const confirm = () => {
    if (canConfirm) resolve.mutate();
  };
  const discard = () => {
    if (!busy && !settled) dismiss.mutate();
  };

  // Reviewing dozens of cards on a computer: the keyboard is faster than the mouse.
  useEffect(() => {
    if (editing || comparing || searching) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey) return;
      if (isTyping(event.target) || document.querySelector("dialog[open]")) return;
      const key = event.key.length === 1 ? event.key.toLowerCase() : event.key;
      const slot = Number(key);
      const pick = Number.isInteger(slot) && slot >= 1 ? shown[slot - 1] : undefined;
      const actions: Record<string, (() => void) | undefined> = {
        Enter: confirm,
        ArrowRight: confirm,
        ArrowLeft: nextCandidate,
        d: discard,
        s: () => {
          setSearching(true);
        },
        e: approval
          ? undefined
          : () => {
              setEditing(true);
            },
      };
      const action = pick
        ? () => {
            setChosen(pick.print_id);
          }
        : actions[key];
      if (!action) return;
      event.preventDefault();
      action();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
    };
  });

  return {
    scan,
    candidates: shown,
    chosen,
    setChosen,
    card,
    variants,
    choice: effective,
    setChoice,
    label,
    photo,
    original,
    resolve,
    dismiss,
    recognise,
    busy,
    settled,
    canConfirm,
    confirm,
    discard,
    nextCandidate,
    editing,
    setEditing,
    comparing,
    setComparing,
    searching,
    setSearching,
    ocr: scan.result?.ocr as ReadNumber | undefined,
    /** Releasing labels: no new corners, and the buttons say what they do here. */
    approving: Boolean(approval),
    confirmKey: approval ? "review.approve" : "review.confirm",
    discardKey: approval ? "review.exclude" : "review.dismiss",
  };
}

export type ReviewModel = ReturnType<typeof useReviewCase>;
